import type { App } from 'vue';
import { createI18n, type VueI18nInstance } from 'vue-i18n';
import CommonDE from './locales/de/core.json' with { type: 'json' };
import CommonEN from './locales/en/core.json' with { type: 'json' };

export interface TranslationResources {
  [key: string]: Record<string, unknown>;
}

export const defaultLanguage = 'de';
export const fallbackLanguage = 'de';

const messages = {
  en: {
    core: CommonEN,
  },
  de: {
    core: CommonDE,
  },
};

const i18n = createI18n({
  legacy: false,
  locale: defaultLanguage,
  fallbackLocale: fallbackLanguage,
  messages,
  allowComposition: true,
  runtimeOnly: false,
  escapeParameter: false,
  missingWarn: false,
  fallbackWarn: false,
});

// Expose i18n instance for i18n-ally
if (typeof window !== 'undefined') {
  (
    window as Window & {
      $i18nAllyConfig?: { name: string; instance: typeof i18n };
    }
  ).$i18nAllyConfig = {
    name: 'vue-i18n',
    instance: i18n,
  };
}

export const getLanguageFromLocalStorage = () => {
  const lang = JSON.parse(localStorage.getItem('locale') as string);
  if (lang?.selectedLanguage) {
    return lang.selectedLanguage;
  }
  return fallbackLanguage;
};

const changeLanguage = (languageCode: 'de' | 'en') => {
  i18n.global.locale.value = languageCode;
  localStorage.setItem(
    'locale',
    JSON.stringify({ selectedLanguage: languageCode })
  );
};

export const TranslationPlugin = {
  install: (
    app: App,
    options?: { additionalResources?: TranslationResources }
  ) => {
    if (options?.additionalResources) {
      Object.entries(options.additionalResources).forEach(
        ([language, resources]) => {
          i18n.global.mergeLocaleMessage(language as 'de' | 'en', resources);
        }
      );
    }

    app.config.globalProperties.$changeLanguage = (languageCode: 'de' | 'en') =>
      changeLanguage(languageCode);
    app.config.globalProperties.$t = i18n.global.t;
    app.config.globalProperties.$i18n =
      i18n.global as unknown as VueI18nInstance;

    const selectedLanguage = getLanguageFromLocalStorage();
    if (selectedLanguage) {
      changeLanguage(selectedLanguage as 'de' | 'en');
    }
    return i18n;
  },
  i18n,
} as const;

export const t: typeof i18n.global.t = i18n.global.t;
export { i18n };
